import type { CoachVetting } from '@/types/coach'

type CoachVettingSnapshot = Pick<
  CoachVetting,
  'status' | 'revision_status' | 'has_unpublished_changes'
>

export interface CoachProfileReviewReminder {
  title: string
  description: string
  actionLabel: string
}

export function getCoachProfileReviewReminder(
  vetting: CoachVettingSnapshot | null | undefined,
): CoachProfileReviewReminder | null {
  if (!vetting) return null

  if (vetting.status === 'draft') {
    return {
      title: 'Your profile is still a draft',
      description:
        'Members cannot find or book you until you finish your profile and submit it for review.',
      actionLabel: 'Finish and submit profile',
    }
  }

  if (vetting.status === 'changes_requested') {
    return {
      title: 'Your profile needs changes',
      description: 'Review the feedback, update your profile, and submit it again for review.',
      actionLabel: 'Review feedback',
    }
  }

  if (vetting.status === 'rejected') {
    return {
      title: 'Your profile has not been approved',
      description: 'Update your profile and submit it again when it is ready for another review.',
      actionLabel: 'Update and resubmit',
    }
  }

  if (
    vetting.status === 'approved' &&
    vetting.has_unpublished_changes &&
    (vetting.revision_status === 'changes_requested' || vetting.revision_status === 'rejected')
  ) {
    return {
      title: 'Your latest profile changes need work',
      description:
        'Your published profile is still live. Review the feedback, update your changes, and resubmit them.',
      actionLabel: 'Review feedback',
    }
  }

  if (
    vetting.status === 'approved' &&
    vetting.has_unpublished_changes &&
    vetting.revision_status !== 'pending_review'
  ) {
    return {
      title: 'Your latest profile changes are not live',
      description:
        'Your published profile is unchanged. Review and submit your saved changes to publish them.',
      actionLabel: 'Review and submit changes',
    }
  }

  return null
}

export function coachProfileReviewNeedsAction(
  vetting: CoachVettingSnapshot | null | undefined,
): boolean {
  return getCoachProfileReviewReminder(vetting) !== null
}
